import { forwardRef, MouseEvent, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutocompleteOption, ParameterPropertyType } from 'src/types'
import { AutoComplete, FormInput, TextField } from '@microservices/wiskey-react-components'
import {
  Add as IncreaseIcon,
  ArrowDropDown as ArrowDropDownIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignRight as AlignRightIcon,
  FormatBold as BoldIcon,
  FormatColorText as FontColorIcon,
  FormatItalic as ItalicIcon,
  Remove as DecreaseIcon,
} from '@mui/icons-material'
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'

import { ColorPicker } from '@components/ColorPicker'

import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'

import { GENERATOR_INPUT_TYPE, GLOBAL_PARAMETER_CODE, REGEX } from '@constants'

import { ColorSettings } from './components/ColorSettings'

export type ExternalTextSettings = TextSettingsStateType

export type AlignmentType = 'left' | 'center' | 'right'
export type FormatType = 'bold' | 'italic'
export type TextParams = {
  fontFamily: string
  fontSize: number | string
  fontColor: string | null
  bgFontColor?: string
  alignment: AlignmentType
  padding?: number | string
  isBold: string
  isItalic: string
  borderColor?: string
}

export const isTextParam = (textParam: string): textParam is keyof TextParams => {
  return [
    'fontFamily',
    'fontSize',
    'fontColor',
    'bgFontColor',
    'alignment',
    'padding',
    'isBold',
    'isItalic',
    'borderColor',
  ].includes(textParam)
}

export type GetTextParamsHandle = {
  getTextParams: () => TextParams
  setValues: (values: TextSettingsStateType) => void
}

type TextSettingsProps = {
  labelText: string
  isEdit?: boolean
  backgroundSettings?: boolean
  borderSettings?: boolean
  paddingSettings?: boolean
  labelPlacement?: 'top' | 'left'
  initialTextState?: TextSettingsStateType
  hasWrapRowText?: boolean
  hasWrapHeaderText?: boolean
  onSetExternalDirty?: (value: ExternalTextSettings) => void
}

export type TextSettingsStateType = {
  fontFamily: AutocompleteOption
  fontSize: string
  formats: FormatType[]
  alignment: AlignmentType
  fontColor: string
  bgFontColor?: string
  borderColor?: string
  padding?: string
}

export const alignIconsData = {
  left: <AlignLeftIcon />,
  center: <AlignCenterIcon />,
  right: <AlignRightIcon />,
}

const mockText = 'Lorem ipsum dolor sit amet consectetur!'

export const INITIAL_TEXT_STATE: TextSettingsStateType = {
  fontFamily: { id: 'Arial', label: 'Arial' },
  fontSize: '14',
  formats: [],
  alignment: 'left',
  fontColor: '#000',
  bgFontColor: 'none',
  borderColor: 'none',
  padding: '0',
}

// Стейт "вытягивается" с помощью useImperativeHandle, чтобы не приходилось иметь свое состояние
// для каждого кейса
const TextSettings = forwardRef<GetTextParamsHandle, TextSettingsProps>(
  (
    {
      labelText,
      isEdit = false,
      isHeaderTextSetting,
      backgroundSettings = true,
      borderSettings = true,
      paddingSettings = false,
      labelPlacement = 'left',
      initialTextState = INITIAL_TEXT_STATE,
      hasWrapRowText = false,
      hasWrapHeaderText = false,
      onSetExternalDirty,
    },
    ref
  ) => {
    const { t } = useTranslation()

    const fontSizeInputRef = useRef<HTMLInputElement | null>(null)

    const [fontFamilyOptions, setFontFamilyOptions] = useState<AutocompleteOption[] | null>(null)
    const [fontFamily, setFontFamily] = useState<AutocompleteOption>(initialTextState.fontFamily)
    const [fontSize, setFontSize] = useState<number>(Number(initialTextState.fontSize))
    const [formats, setFormats] = useState<FormatType[]>(initialTextState.formats)
    const [alignment, setAlignment] = useState<AlignmentType>(initialTextState.alignment)
    const [fontColor, setFontColor] = useState<string>(initialTextState.fontColor)
    const [bgFontColor, setBgFontColor] = useState<string | undefined>(initialTextState.bgFontColor)
    const [borderColor, setBorderColor] = useState<string | undefined>(initialTextState.borderColor)
    const [padding, setPadding] = useState<number>(Number(initialTextState.padding ?? 0))

    const [alignMenuAnchor, setAlignMenuAnchor] = useState<null | HTMLElement>(null)
    const alignMenuOpen = Boolean(alignMenuAnchor)
    const [openFontColorPicker, setOpenFontColorPicker] = useState(false)
  const fontColorButtonRef = useRef<HTMLButtonElement | null>(null)
    const [isDirty, setDirty] = useState(false)

    const { data: globalFontVariants }: { data: ParameterPropertyType[] } =
      useFetchParameterPropertiesQuery({
        code: GLOBAL_PARAMETER_CODE.GLOBAL_FONT_VARIANTS,
      })

    useEffect(() => {
      if (initialTextState) {
        handleSetValues(initialTextState)
      }
    }, [initialTextState])

    useEffect(() => {
      if (fontSizeInputRef.current) {
        fontSizeInputRef.current.value = `${fontSize}`
      }
    }, [fontSize])

    useEffect(() => {
      if (!globalFontVariants) {
        return
      }

      setFontFamilyOptions(
        globalFontVariants.map(
          fontVariant => ({ id: fontVariant.key, label: fontVariant.key } as AutocompleteOption)
        )
      )
    }, [globalFontVariants])

    useImperativeHandle(ref, () => ({
      getTextParams: () => ({
        fontFamily: fontFamily.label,
        fontSize: fontSize.toString(),
        fontColor,
        alignment,
        isBold: String(Boolean(formats.find(format => format === 'bold'))),
        isItalic: String(Boolean(formats.find(format => format === 'italic'))),
        ...(paddingSettings ? { padding: padding.toString() } : {}),
        ...(backgroundSettings ? { bgFontColor } : {}),
        ...(borderSettings ? { borderColor } : {}),
      }),
      setValues: handleSetValues,
    }))

    const handleSetValues = (values: TextSettingsStateType) => {
      setFontFamily(values.fontFamily)
      setFontSize(Number(values.fontSize))
      setFormats(values.formats)
      setAlignment(values.alignment)
      setFontColor(values.fontColor)
      setPadding(Number(values.padding ?? 0))

      if (backgroundSettings) {
        setBgFontColor(values.bgFontColor)
      }

      if (borderSettings) {
        setBorderColor(values.borderColor)
      }

      onSetExternalDirty?.({
        fontFamily: values.fontFamily,
        fontSize: values.fontSize?.toString(),
        formats: values.formats,
        alignment: values.alignment,
        fontColor: values.fontColor,
        ...(paddingSettings ? { padding: values.padding?.toString() ?? '0' } : {}),
        ...(backgroundSettings && values.bgFontColor ? { bgFontColor: values.bgFontColor } : {}),
        ...(borderSettings && values.borderColor ? { borderColor: values.borderColor } : {}),
      })
    }

    const handleAlignMenuAnchorClick = (e: React.MouseEvent<HTMLElement>) => {
      setAlignMenuAnchor(e.currentTarget)
    }
    const handleAlignMenuAnchorClose = () => {
      setAlignMenuAnchor(null)
    }

    const handleFormatChange = (event: MouseEvent<HTMLElement>, newFormats: FormatType[]) => {
      setFormats(newFormats)
      onSetExternalDirty?.({
        fontFamily,
        fontSize: fontSize.toString(),
        formats: newFormats,
        alignment,
        fontColor,
        ...(paddingSettings ? { padding: padding.toString() } : {}),
        ...(backgroundSettings ? { bgFontColor } : {}),
        ...(borderSettings ? { borderColor } : {}),
      })
    }

    const handleAlignmentChange = (newAlignment: AlignmentType) => {
      setAlignment(newAlignment)
      onSetExternalDirty?.({
        fontFamily,
        fontSize: fontSize.toString(),
        formats,
        alignment: newAlignment,
        fontColor,
        ...(paddingSettings ? { padding: padding.toString() } : {}),
        ...(backgroundSettings ? { bgFontColor } : {}),
        ...(borderSettings ? { borderColor } : {}),
      })
      handleAlignMenuAnchorClose()
    }

    const handleFontFamilyChange = (value: AutocompleteOption) => {
      setFontFamily(value)
      onSetExternalDirty?.({
        fontFamily: value,
        fontSize: fontSize.toString(),
        formats,
        alignment,
        fontColor,
        ...(paddingSettings ? { padding: padding.toString() } : {}),
        ...(backgroundSettings ? { bgFontColor } : {}),
        ...(borderSettings ? { borderColor } : {}),
      })
    }

    const handleFontSizeChange = (value: number) => {
      if (!fontSizeInputRef.current) {
        return
      }

      if (value === 0) {
        fontSizeInputRef.current.value = `${fontSize}`

        return
      }

      fontSizeInputRef.current.value = `${value}`

      setFontSize(value)
      onSetExternalDirty?.({
        fontFamily,
        fontSize: value.toString(),
        formats,
        alignment,
        fontColor,
        ...(paddingSettings ? { padding: padding.toString() } : {}),
        ...(backgroundSettings ? { bgFontColor } : {}),
        ...(borderSettings ? { borderColor } : {}),
      })
    }

    const handlePaddingChange = (next: number) => {
      const safe = Number.isFinite(next) && next >= 0 ? next : 0
      setPadding(safe)
      onSetExternalDirty?.({
        fontFamily,
        fontSize: fontSize.toString(),
        formats,
        alignment,
        fontColor,
        ...(paddingSettings ? { padding: safe.toString() } : {}),
        ...(backgroundSettings ? { bgFontColor } : {}),
        ...(borderSettings ? { borderColor } : {}),
      })
    }

    const handleChangeFontBgColor = (value: string | undefined) => {
      const nextValue = value ?? ''
      setBgFontColor(nextValue)
      onSetExternalDirty?.({
        fontFamily,
        fontSize: fontSize.toString(),
        formats,
        alignment,
        fontColor,
        ...(backgroundSettings ? { bgFontColor: nextValue } : {}),
        ...(borderSettings ? { borderColor: nextValue } : {}),
      })
    }

    const handleChangeFontColor = (value: string) => {
      setFontColor(value)
      onSetExternalDirty?.({
        fontFamily,
        fontSize: fontSize.toString(),
        formats,
        alignment,
        fontColor: value,
        ...(backgroundSettings ? { bgFontColor } : {}),
        ...(borderSettings ? { borderColor } : {}),
      })
    }

    const handleChangeBorderColor = (value: string | undefined) => {
      const nextValue = value ?? ''
      setBorderColor(nextValue)
      onSetExternalDirty?.({
        fontFamily,
        fontSize: fontSize.toString(),
        formats,
        alignment,
        fontColor,
        ...(backgroundSettings ? { bgFontColor: nextValue } : {}),
        ...(borderSettings ? { borderColor: nextValue } : {}),
      })
    }

    const isBold = formats.some(f => f === 'bold')
    const isItalic = formats.some(f => f === 'italic')
    const hasBackground = bgFontColor && bgFontColor.toLowerCase() !== 'none'
    const hasBorder = borderColor && borderColor.toLowerCase() !== 'none'

    const sectionLabelSx = {
      color: 'text.secondary',
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    } as const

    return (
      <>
        <Menu anchorEl={alignMenuAnchor} open={alignMenuOpen} onClose={handleAlignMenuAnchorClose}>
          <MenuItem disableRipple onClick={() => handleAlignmentChange('left')}>
            <AlignLeftIcon sx={{ mr: 1 }} />
            {t('parameters.visual.alignOptions.left')}
          </MenuItem>
          <MenuItem disableRipple onClick={() => handleAlignmentChange('center')}>
            <AlignCenterIcon sx={{ mr: 1 }} />
            {t('parameters.visual.alignOptions.center')}
          </MenuItem>
          <MenuItem disableRipple onClick={() => handleAlignmentChange('right')}>
            <AlignRightIcon sx={{ mr: 1 }} />
            {t('parameters.visual.alignOptions.right')}
          </MenuItem>
        </Menu>

        <Stack spacing={2}>
          <Box>
            <Stack direction='row' alignItems='center' justifyContent='space-between' mb={0.75}>
              <Typography sx={sectionLabelSx}>{labelText}</Typography>
              <Stack direction='row' spacing={1}>
                {hasWrapRowText && (
                  <FormInput
                    disabled={!isEdit}
                    formInputContainerSx={{ mb: 0 }}
                    inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
                    label={t('columnInputs.wrapRowText.label')}
                    labelPlacement='end'
                    labelSx={{ width: 'auto', minWidth: 130 }}
                    name='hasWrapRowText'
                  />
                )}
                {hasWrapHeaderText && (
                  <FormInput
                    disabled={!isEdit}
                    formInputContainerSx={{ mb: 0 }}
                    inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
                    label={t('columnInputs.WrapHeaderText.label')}
                    labelPlacement='end'
                    labelSx={{ width: 'auto', minWidth: 130 }}
                    name='hasWrapHeaderText'
                  />
                )}
              </Stack>
            </Stack>
            <Paper
              variant='outlined'
              sx={{
                borderRadius: 1.5,
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                rowGap: 0.5,
                columnGap: 1,
                bgcolor: 'background.paper',
              }}
            >
              <AutoComplete
                disableClearable
                disabled={!isEdit}
                options={fontFamilyOptions ?? []}
                size='small'
                sx={{ minWidth: 200, flexGrow: 1 }}
                value={fontFamily}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant='outlined'
                    inputProps={{
                      ...params.inputProps,
                      sx: {
                        fontFamily: fontFamily.label ?? 'inherit',
                        p: 0,
                      },
                    }}
                    sx={{
                      color: theme => theme.palette.grey[600],
                      border: 'none',
                      '& fieldset': { border: 'none' },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li key={option.id} style={{ fontFamily: option.label ?? 'inherit' }} {...props}>
                    {option.label}
                  </li>
                )}
                onChange={handleFontFamilyChange}
              />

              <Divider orientation='vertical' flexItem sx={{ my: 0.5 }} />

              <Stack direction='row' alignItems='center'>
                <IconButton
                  disabled={!isEdit}
                  size='small'
                  onClick={() => handleFontSizeChange(Number(fontSize) - 1)}
                >
                  <DecreaseIcon fontSize='small' />
                </IconButton>
                <TextField
                  disabled={!isEdit}
                  inputProps={{
                    sx: { px: 1, py: 0.5, width: 32, textAlign: 'center' },
                    ref: fontSizeInputRef,
                  }}
                  sx={{ width: 56, mx: 0.5 }}
                  onBlur={e => handleFontSizeChange(Number(e.target.value))}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-ignore
                      handleFontSizeChange(Number(e.target.value))
                    }
                  }}
                />
                <IconButton
                  disabled={!isEdit}
                  size='small'
                  onClick={() => handleFontSizeChange(Number(fontSize) + 1)}
                >
                  <IncreaseIcon fontSize='small' />
                </IconButton>
              </Stack>

              <Divider orientation='vertical' flexItem sx={{ my: 0.5 }} />

              <ToggleButtonGroup
                disabled={!isEdit}
                value={formats}
                size='small'
                sx={{
                  'button.Mui-disabled': { border: 'none !important' },
                }}
                onChange={handleFormatChange}
              >
                <ToggleButton sx={{ px: 1, border: 'none', borderRadius: 1 }} value='bold'>
                  <BoldIcon fontSize='small' />
                </ToggleButton>
                <ToggleButton sx={{ px: 1, border: 'none', borderRadius: 1 }} value='italic'>
                  <ItalicIcon fontSize='small' />
                </ToggleButton>
              </ToggleButtonGroup>

              <Divider orientation='vertical' flexItem sx={{ my: 0.5 }} />

              <IconButton
                ref={fontColorButtonRef}
                disabled={!isEdit}
                size='small'
                sx={{ position: 'relative' }}
                onClick={() => setOpenFontColorPicker(true)}
              >
                <FontColorIcon fontSize='small' sx={{ color: fontColor || 'inherit' }} />
                <ColorPicker
                  color={fontColor ?? '#000000'}
                  open={openFontColorPicker}
                  anchorEl={fontColorButtonRef.current}
                  onChange={handleChangeFontColor}
                  onClickOutside={() => setOpenFontColorPicker(false)}
                />
              </IconButton>
              <IconButton
                disabled={!isEdit}
                size='small'
                onClick={handleAlignMenuAnchorClick}
              >
                {alignIconsData[`${alignment}`]}
                <ArrowDropDownIcon fontSize='small' />
              </IconButton>
            </Paper>
          </Box>

          {isHeaderTextSetting && (
            <Box>
              <Typography sx={sectionLabelSx} mb={0.75}>
                {t('parameters.visual.header')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormInput
                    disabled={!isEdit}
                    inputType={GENERATOR_INPUT_TYPE.INPUT}
                    label={t('parameters.visual.headerHeight')}
                    labelPlacement='start'
                    labelSx={{ width: 150, minWidth: 150, color: '#808080' }}
                    maxLengthInput={3}
                    name='headerHeight'
                    replacePattern={REGEX.REPLACE_ONLY_NUMBER}
                    type='number'
                    formInputContainerSx={{ flexWrap: 'nowrap', mb: 0 }}
                    textFieldInputProps={{ inputProps: { min: 30, max: 150 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormInput
                    disabled={!isEdit}
                    inputType={GENERATOR_INPUT_TYPE.INPUT}
                    label={t('parameters.visual.headerGroupHeight')}
                    labelPlacement='start'
                    labelSx={{ width: 150, minWidth: 150, color: '#808080' }}
                    maxLengthInput={3}
                    name='headerGroupHeight'
                    replacePattern={REGEX.REPLACE_ONLY_NUMBER}
                    type='number'
                    formInputContainerSx={{ flexWrap: 'nowrap', mb: 0 }}
                    textFieldInputProps={{ inputProps: { min: 30, max: 150 } }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {(backgroundSettings || borderSettings) && (
            <Box>
              <Typography sx={sectionLabelSx} mb={0.75}>
                {t('parameters.visual.background')} / {t('parameters.visual.border')}
              </Typography>
              <Grid container spacing={2}>
                {backgroundSettings && (
                  <Grid item xs={12} md={6}>
                    <ColorSettings
                      clearableColor
                      color={bgFontColor}
                      isEdit={isEdit}
                      label={t('parameters.visual.background')}
                      labelPlacement='top'
                      onChangeColor={handleChangeFontBgColor}
                    />
                  </Grid>
                )}
                {borderSettings && (
                  <Grid item xs={12} md={6}>
                    <ColorSettings
                      clearableColor
                      color={borderColor}
                      isEdit={isEdit}
                      label={t('parameters.visual.border')}
                      labelPlacement='top'
                      onChangeColor={handleChangeBorderColor}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {paddingSettings && (
            <Box>
              <Typography sx={sectionLabelSx} mb={0.75}>
                Padding
              </Typography>
              <Stack direction='row' alignItems='center' spacing={2}>
                <TextField
                  disabled={!isEdit}
                  size='small'
                  type='number'
                  value={String(padding)}
                  onChange={(e: any) => handlePaddingChange(Number(e?.target?.value ?? 0))}
                  inputProps={{ min: 0, max: 999 }}
                  sx={{ width: 140 }}
                />
                <Typography variant='caption' color='text.secondary'>
                  px
                </Typography>
              </Stack>
            </Box>
          )}

          <Box>
            <Typography sx={sectionLabelSx} mb={0.75}>
              {t('parameters.visual.exampleText')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <Box
                sx={{
                  backgroundColor: hasBackground ? bgFontColor : 'transparent',
                  border: hasBorder ? `1px solid ${borderColor}` : undefined,
                  borderRadius: 1,
                  p: `${padding}px`,
                  transition: 'all .15s ease',
                }}
              >
                <Typography
                  sx={{
                    width: '100%',
                    textAlign: alignment,
                    fontSize: `${fontSize}px`,
                    color: fontColor,
                    fontFamily: fontFamily.label,
                    fontWeight: isBold ? 'bold' : 'normal',
                    fontStyle: isItalic ? 'italic' : 'normal',
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                  }}
                >
                  {mockText}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>
      </>
    )
  }
)

TextSettings.displayName = 'TextSettings'
export default TextSettings
